import { ConnectEmailMethod, CookieKey, NewsletterOptInSource, SignUpSource } from '@/constants';
import { triggerHook } from '@/fetchers';
import { useAuth, useGeo, useGlobalState } from '@/hooks';
import { REFRESH_TOKEN_EXPIRE_DAYS } from '@/hooks/useAuth.hook';
import { DEFAULT_EXPIRE_DAYS } from '@/hooks/useAuth.hook';
import { LoginMethod } from '@/interfaces';
import { PmtOnboardingPopupCta } from '@/interfaces/tracking';
import { EmailSyncGoogleApiScopes } from '@/modules/pmt/constants/googleApi.constants';
import { pmtQueryParams } from '@/modules/pmt/constants/queryParameters.constants';
import { useEmailSyncContext } from '@/modules/pmt/context/EmailSync.context';
import { useMyParcelsContext } from '@/modules/pmt/context/MyParcels.context';
import { getGaClientId, getSessionId } from '@/modules/pmt/utils/ga4.utils';
import { hasGrantedAllScopesGoogle } from '@/modules/pmt/utils/googleLogin.utils';
import { gtmConnectEmailSuccess, gtmSignUpSuccess } from '@/utils';
import { getUtmParams } from '@/utils/path.utils';
import { useGoogleLogin } from '@react-oauth/google';
import { useStatsigClient } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { type ReactNode, useEffect, useRef } from 'react';

const { IsFromEmsLandingPage, EmsLandingPageIdentifier, Code, Scope, AuthUser, Prompt } = pmtQueryParams;
const GoogleOauthParams = [Code, Scope, AuthUser, Prompt];
const EMSAuthParams = [IsFromEmsLandingPage, EmsLandingPageIdentifier, ...GoogleOauthParams];

/**
 * GoogleAuthTrigger is created to handle redirection flow from EMS Landing Page -> PMT:
 * https://www.pivotaltracker.com/n/projects/2649247/stories/187873450
 */
const GoogleAuthTrigger = ({ children }: { children: ReactNode }) => {
  const { geoInfo } = useGeo();
  const { userInfo, setShowPMTPopUp, isFollowingLinkedIn } = useGlobalState();
  const { logEvent } = useStatsigClient();
  const { query, push, pathname } = useRouter();
  const { isESAllowed, setSyncingAccount } = useEmailSyncContext();
  const { authEMS, PMLogin } = useAuth();
  const { pmtOnboardingPopUp } = useMyParcelsContext();

  const redirectUri = useRef<string>('');

  useEffect(() => {
    redirectUri.current = `${window.location.protocol}//${window.location.host}/track-my-parcel`;
  }, []);

  const authenticateES = async (code: string, scope: string) => {
    const utmParams = JSON.parse(Cookies.get(CookieKey.UtmParams) ?? '{}') as Record<string, string>;

    // user already logged-in with email_sync_opt_in = true, we don't go through authentication process again
    try {
      // create account on PMT + email sync enabled
      const authResponse = await authEMS({
        scope,
        code,
        signUpSource: SignUpSource.ConsumerWeb,
        newsletterOptIn: false,
        country: userInfo?.country ?? geoInfo.country,
        redirectUri: redirectUri.current,
        gaClientId: getGaClientId(),
        gaSessionId: getSessionId(true),
        eventTimestamp: new Date().toString(),
        cta: PmtOnboardingPopupCta.LandingPage,
        utmParams,
      });

      // FORCE LOGIN WITH CURRENT AUTH RESPONSE
      // set access token + refresh token --> merge shipment --> set user id
      Cookies.set(CookieKey.AccessToken, authResponse.access_token, { expires: DEFAULT_EXPIRE_DAYS });
      Cookies.set(CookieKey.RefreshToken, authResponse.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });

      Cookies.set(CookieKey.UserId, authResponse.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

      PMLogin(authResponse.user_info);

      const landingPageIdentifier = query[EmsLandingPageIdentifier] as string;
      // connect_email_success
      gtmConnectEmailSuccess(ConnectEmailMethod.LandingPage, landingPageIdentifier);

      // Only trigger zap if user is not logged-in previously:
      if (!userInfo) {
        gtmSignUpSuccess({
          cta: PmtOnboardingPopupCta.LandingPage,
          variant: landingPageIdentifier,
          click: true,
          authenticationMethod: LoginMethod.Google,
        });
        logEvent('sign_up_success', undefined, {
          authenticationMethod: LoginMethod.Google,
          cta: PmtOnboardingPopupCta.LandingPage,
          variant: landingPageIdentifier,
          click: 'true',
        });
        void triggerHook({
          hookId: pmtOnboardingPopUp.zapierHookId,
          data: {
            firstName: authResponse.user_info.first_name,
            lastName: authResponse.user_info.last_name,
            email: authResponse.user_info.email,
            authenticationMethod: authResponse.user_info.authentication_method,
            profilePictureUrl: authResponse.user_info.profile_picture_url,
            verificationStatus: true,
            newsletterOptIn: authResponse.user_info.newsletter_opt_in ? 'Yes' : 'No',
            newsletterOptInSource: NewsletterOptInSource.PMT_WEBSITE,
            linkedInFollowStatus: isFollowingLinkedIn,
            userType: authResponse.user_info.user_type,
            country: authResponse.user_info.country,
            emailSyncOptIn: authResponse.user_info.email_sync_opt_in,
          },
        });
      }

      // trigger InboxAccessDialog
      const hasAllAccess = hasGrantedAllScopesGoogle(EmailSyncGoogleApiScopes, scope);
      Cookies.set(CookieKey.HasGrantedAllAccess, hasAllAccess.toString());

      // trigger SyncProgressBanner
      if (authResponse.user_info.email_sync_opt_in) setSyncingAccount(code);

      // trigger TrackingOnboardingPopup
      Cookies.set(CookieKey.PmtVerificationStatus, 'true');
      setShowPMTPopUp(true);

      EMSAuthParams.forEach((param) => delete query[param]);
      void push({ pathname, query }, undefined, { shallow: true });

      // Remove utm params from cookie after user completed GG auth flow
      Cookies.remove(CookieKey.UtmParams);
    } catch (err) {
      console.error('[GoogleAuthTrigger] - authenticate failed', err);
    }
  };

  const loginES = useGoogleLogin({
    onError: (err) => console.error('[GoogleAuthTrigger] - Google Auth failed', err),
    // ES flow requires 'auth-code'
    flow: 'auth-code',
    scope: EmailSyncGoogleApiScopes,
    ux_mode: 'redirect',
    redirect_uri: redirectUri.current,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: must wait for window.google.accounts.oauth to populate
  useEffect(() => {
    if (userInfo?.emailSyncOptIn || query[IsFromEmsLandingPage] !== 'true' || isESAllowed !== 'allowed') return;
    const utmParams = getUtmParams(query);

    // Temporary save utm params in cookie to use after user completed GG auth flow
    Cookies.set(CookieKey.UtmParams, JSON.stringify(utmParams));

    loginES();
  }, [
    query[IsFromEmsLandingPage],
    isESAllowed,
    loginES,
    query,
    // @ts-expect-error: google is not typed yet
    // wait for google oauth2 to populate via GoogleOauthProvider
    // https://github.com/MomenSherif/react-oauth/blob/master/packages/%40react-oauth/google/src/hooks/useGoogleLogin.ts#L99
    (typeof window !== 'undefined' && window.google?.accounts?.oauth2) || undefined,
  ]);

  useEffect(() => {
    const hasGoogleOauthParams = GoogleOauthParams.every((val) => !!query[val]);
    if (!hasGoogleOauthParams || !geoInfo.fetched) return;
    // Trigger PMT's authentication flow after Google Oauth flow
    void authenticateES(query[Code] as string, query[Scope] as string);
  }, [query, geoInfo.fetched]);

  return <>{children}</>;
};

export { GoogleAuthTrigger };
